<!--
	@component
	Svelte 5 wrapper around vanilla flatpickr.
	Replaces svelte-flatpickr which is Svelte 4 only.

	Usage:
	```svelte
	<Flatpickr bind:value options={{ dateFormat: 'd.m.Y' }} onchange={handleChange} />
	```
-->
<script lang="ts">
	import { untrack } from 'svelte'
	import flatpickr from 'flatpickr'
	import type { Instance } from 'flatpickr/dist/types/instance'
	import type { Options, DateOption, Hook } from 'flatpickr/dist/types/options'

	type HookCallback = (selectedDates: Date[], dateStr: string, instance: Instance) => void

	interface Props {
		/** Current date value — bindable for two-way sync */
		value?: DateOption | DateOption[]
		/** Formatted string representation of the current value */
		formattedValue?: string
		/** Flatpickr configuration options */
		options?: Options
		/** The flatpickr calendar container element */
		element?: HTMLElement
		/** The underlying input element */
		input?: HTMLInputElement
		/** The flatpickr instance (also exposed as `flatpickr`) */
		fp?: Instance
		/** Date format used when syncing value back via setDate */
		dateFormat?: string
		// Callback props (replace Svelte 4 createEventDispatcher)
		onchange?: HookCallback
		onopen?: HookCallback
		onclose?: HookCallback
		onready?: HookCallback
		onmonthchange?: HookCallback
		onyearchange?: HookCallback
		onvalueupdate?: HookCallback
		ondaycreate?: HookCallback
		// Pass-through HTML attributes for the input element
		[key: string]: unknown
	}

	const hookNames = [
		'onChange',
		'onOpen',
		'onClose',
		'onMonthChange',
		'onYearChange',
		'onReady',
		'onValueUpdate',
		'onDayCreate'
	] as const

	/* eslint-disable no-useless-assignment -- $bindable defaults are used by parent via bind: */
	let {
		value = $bindable(undefined),
		formattedValue = $bindable(''),
		options = {},
		element = $bindable(undefined),
		input = $bindable(undefined),
		fp = $bindable(undefined),
		dateFormat = undefined,
		onchange,
		onopen,
		onclose,
		onready,
		onmonthchange,
		onyearchange,
		onvalueupdate,
		ondaycreate,
		...restProps
	}: Props = $props()
	/* eslint-enable no-useless-assignment */

	let inputElement: HTMLInputElement | undefined = $state()
	let ready = $state(false)
	// Tracks the last value set by our own updateValue, so the value-sync effect
	// can distinguish internal vs external value changes.
	let lastInternalValue: DateOption | DateOption[] | undefined

	/**
	 * Get the callback for a given hook name by reading the current prop value.
	 * Called at hook invocation time (not setup time) so updated props are honored.
	 * Reads are untracked because this is called from within closures that fire
	 * outside of any reactive context.
	 */
	function getCallback(hookName: string): HookCallback | undefined {
		switch (hookName) {
			case 'onChange':
				return onchange
			case 'onOpen':
				return onopen
			case 'onClose':
				return onclose
			case 'onReady':
				return onready
			case 'onMonthChange':
				return onmonthchange
			case 'onYearChange':
				return onyearchange
			case 'onValueUpdate':
				return onvalueupdate
			case 'onDayCreate':
				return ondaycreate
			default:
				return undefined
		}
	}

	/**
	 * Determine the appropriate value based on flatpickr mode.
	 * Single mode returns one Date, range/multiple returns the array.
	 */
	function getModeValue(instance: Instance, selectedDates: Date[]): DateOption | DateOption[] {
		const mode = instance?.config?.mode ?? 'single'
		// Copy array to avoid holding a reference to flatpickr's internal mutable array
		return mode === 'single' ? selectedDates[0] : [...selectedDates]
	}

	/**
	 * Compare two date values for equality to prevent unnecessary updates / infinite loops.
	 */
	function areValuesEqual(
		v1: DateOption | DateOption[] | undefined,
		v2: DateOption | DateOption[] | undefined
	): boolean {
		if (v1 == v2) return true

		if (v1 instanceof Date && v2 instanceof Date && v1.valueOf() === v2.valueOf()) {
			return true
		}

		if (
			Array.isArray(v1) &&
			Array.isArray(v2) &&
			v1.length === v2.length &&
			v1.every((val, i) => {
				const other = v2[i]
				if (val instanceof Date && other instanceof Date) {
					return val.valueOf() === other.valueOf()
				}
				return val === other
			})
		) {
			return true
		}

		return false
	}

	/**
	 * Called by the flatpickr onChange hook. Updates value and formattedValue.
	 */
	function updateValue(newDates: Date[], dateStr: string, instance: Instance): void {
		const newModeValue = getModeValue(instance, newDates)
		// Only update if actually different (and at least one side is truthy) to avoid loops
		if (!areValuesEqual(value, newModeValue) && (value || newModeValue)) {
			lastInternalValue = newModeValue
			value = newModeValue
		}
		formattedValue = dateStr
	}

	/**
	 * Build options object with hook arrays that fire our callbacks and update value.
	 * Each firer reads the callback prop at invocation time via getCallback(),
	 * so updated props are always honored.
	 */
	function buildHookedOptions(opts: Options): Options {
		const result: Options = { ...opts }

		for (const hookName of hookNames) {
			const firer: Hook = (selectedDates, dateStr, instance) => {
				getCallback(hookName)?.(selectedDates, dateStr, instance)
			}

			const existing = result[hookName as keyof Options]
			if (existing) {
				if (Array.isArray(existing)) {
					;(result as Record<string, unknown>)[hookName] = [...(existing as Hook[]), firer]
				} else {
					;(result as Record<string, unknown>)[hookName] = [existing as Hook, firer]
				}
			} else {
				;(result as Record<string, unknown>)[hookName] = [firer]
			}
		}

		// Ensure onChange also calls updateValue
		const onChangeArr = (result as Record<string, unknown>).onChange as Hook[]
		if (!onChangeArr.includes(updateValue)) {
			onChangeArr.push(updateValue)
		}

		return result
	}

	// Main effect: create/destroy flatpickr instance.
	// Dependencies: inputElement, options. NOT value or callback props.
	$effect(() => {
		if (!inputElement) return

		// Read options (tracked — instance recreated when options change)
		const opts = buildHookedOptions(options)

		// Push an onReady handler that syncs initial value and marks ready.
		// Use untrack for value reads to avoid making this effect depend on value.
		const onReadyArr = (opts as Record<string, unknown>).onReady as Hook[]
		onReadyArr.push((selectedDates: Date[], dateStr: string, _instance: Instance) => {
			untrack(() => {
				if (value === undefined) {
					updateValue(selectedDates, dateStr, _instance)
				}
			})
			queueMicrotask(() => {
				ready = true
			})
		})

		const instance = flatpickr(inputElement, opts)

		fp = instance
		element = instance.calendarContainer
		input = instance.input as HTMLInputElement

		// Set initial value if provided (untracked to avoid depending on value)
		untrack(() => {
			if (value !== undefined) {
				instance.setDate(value as DateOption | DateOption[], false, dateFormat)
				// Track initial value so the sync effect can detect external clear (value → undefined)
				lastInternalValue = value
			}
		})

		return () => {
			instance.destroy()
			fp = undefined
			ready = false
		}
	})

	// Sync external value changes into flatpickr.
	// Skips if value was just set by our own updateValue (to avoid disrupting
	// flatpickr's internal state during range/multiple selection).
	$effect(() => {
		const currentValue = value
		if (fp && ready) {
			// If this value was set by our own updateValue, skip the sync
			if (areValuesEqual(currentValue, lastInternalValue)) return
			if (currentValue === undefined || currentValue === null) {
				fp.clear()
				fp.input.value = ''
				formattedValue = ''
			} else {
				const fpValue = getModeValue(fp, fp.selectedDates)
				if (!areValuesEqual(currentValue, fpValue)) {
					fp.setDate(currentValue as DateOption | DateOption[], false, dateFormat)
				}
			}
		}
	})

	// Options changes are handled by the main $effect above, which depends on `options`
	// and will destroy/recreate the flatpickr instance when options change.
	// We intentionally do NOT use fp.set() to update options in-place, because
	// fp.set() for hook keys replaces hooks and can disrupt range/multiple selection state.
</script>

<input bind:this={inputElement} type="text" {...restProps} />
